import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { LoadingController, ToastController, AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Letter, uyirLetters, meiLetters } from './words';
import { Phrase, commonPhrases } from './phrases';
import { trigger, transition, style, animate } from '@angular/animations';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Component({
  selector: 'app-speak',
  templateUrl: './speak.page.html',
  styleUrls: ['./speak.page.scss'],
  animations: [
    trigger('pulseAnimation', [
      transition('false => true', [
        style({ transform: 'scale(1)' }),
        animate('300ms ease-out', style({ transform: 'scale(1.05)' })),
        animate('200ms ease-in', style({ transform: 'scale(1)' }))
      ])
    ]),
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('messageAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('slideUpAnimation', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('300ms ease-out', style({ transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class SpeakPage implements OnInit, OnDestroy {
  recognizedText: string = '';
  currentSentence: string = '';
  previousText: string = '';
  recognition: any;
  isRecognizing: boolean = false;
  isProcessing: boolean = false;
  conversations: Array<{ text: string, isUser: boolean }> = [];
  currentLetter: Letter | null = null;
  currentLetterType: 'uyir' | 'mei' | 'sentence' = 'uyir'; // Added 'sentence' type
  isCorrect: boolean = false;
  letterSet: Letter[] = [];
  DEBUG = true;
  buttonState: 'start' | 'next' = 'start';
  isSpeechSupported: boolean = false;
  conversationHistory: Array<{ 
    text: string, 
    isUser: boolean, 
    timestamp: Date,
    transliteration?: string,
    mode?: 'freeflow' | 'phrases' // Add mode to distinguish between types
  }> = [];
  mainSegment: 'word' | 'sentence' = 'word';
  wordSubSegment: 'uyir' | 'mei' = 'uyir';
  useGoogleCloud: boolean = false;
  private _audioInitialized = false;
  romanTransliteration: string = '';
  romanTransliterationPhrase: string = '';
  sentenceSubSegment: 'freeflow' | 'phrases' = 'phrases';
  currentPhrase: Phrase | null = null;
  
  get audioInitialized(): boolean {
    return this._audioInitialized;
  }

  get isRecording(): boolean {
    return this.isListening || this.isProcessing;
  }

  get isMediaRecording(): boolean {
    return this.mediaRecorder?.state === 'recording' || false;
  }

  private audioContext: AudioContext | null = null;
  private audioBuffers: { [key: string]: AudioBuffer } = {};
  private readonly audioFiles = {
    success: 'assets/sounds/success.mp3',
    wrong: 'assets/sounds/wrong.mp3',
    click: 'assets/sounds/click.mp3',
  };
  private debounceTimeout: any;
  private mediaRecorder?: MediaRecorder;
  private audioChunks: Blob[] = [];
  private silenceTimer: any;
  private readonly SILENCE_DURATION = 1500; // 1.5 seconds of silence before stopping
  private isUserSpeaking = false;
  private audioAnalyser: AnalyserNode | null = null;
  private analyserData: Uint8Array | null = null;
  private recordingTimeout: any;
  private isMobileDevice: boolean = false;
  isListening: boolean = false;

  constructor(
    private loadingController: LoadingController,
    private toastController: ToastController,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private alertController: AlertController
  ) {
    this.checkDeviceAndSetup();
  }

  private checkDeviceAndSetup() {
    // Check if device is mobile
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Check if it's Safari browser (both mobile and desktop)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Set speech recognition method based on device and browser
    if (this.isMobileDevice || isSafari) {
      this.useGoogleCloud = true;
    } else {
      // Check Web Speech API support for desktop (non-Safari)
      this.isSpeechSupported = !!(
        (window as any).SpeechRecognition || 
        (window as any).webkitSpeechRecognition || 
        (window as any).mozSpeechRecognition || 
        (window as any).msSpeechRecognition
      );
      this.useGoogleCloud = false;
    }
  }

  private async initAudio() {
    if (this._audioInitialized) return;
    
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      await this.loadAudioFiles();
      this._audioInitialized = true;
    } catch (error) {
      console.error('Failed to initialize audio:', error);
    }
  }

  private async loadAudioFiles() {
    try {
      const loadPromises = Object.entries(this.audioFiles).map(async ([key, path]) => {
        const response = await fetch(path);
        const arrayBuffer = await response.arrayBuffer();
        if (this.audioContext) {
          this.audioBuffers[key] = await this.audioContext.decodeAudioData(arrayBuffer);
        }
      });

      await Promise.all(loadPromises);
    } catch (error) {
      console.error('Failed to load audio files:', error);
    }
  }

  async playAudio(soundName: 'success' | 'wrong' | 'click') {
    try {
      // Initialize audio on first user interaction
      if (!this._audioInitialized) {
        await this.initAudio();
      }

      if (this.audioContext?.state === 'suspended') {
        await this.audioContext.resume();
      }

      if (this.audioContext && this.audioBuffers[soundName]) {
        const source = this.audioContext.createBufferSource();
        source.buffer = this.audioBuffers[soundName];
        source.connect(this.audioContext.destination);
        source.start(0);
      }
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  }

  async showToast(message: string, color: string = 'primary', duration: number = 2000) {
    const toast = await this.toastController.create({
      message,
      duration,
      color,
      position: 'bottom',
      cssClass: 'custom-toast',
      buttons: [
        {
          text: this.translate.instant('OK'),
          role: this.translate.instant('Cancel')
        }
      ]
    });

    await toast.present();
    return toast.onDidDismiss();
  }

  getButtonColor() {
    return this.buttonState === 'next' ? 'tertiary' : 'primary';
  }

  getButtonIcon() {
    return this.buttonState === 'next' ? 'arrow-forward-circle-outline' : 'mic';
  }

  getButtonText() {
    return this.buttonState === 'next' ? this.translate.instant('Next') : this.translate.instant('Start Speaking');
  }

  private async requestMicrophonePermission(): Promise<MediaStream | null> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      return stream;
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        this.showToast(
          this.translate.instant('Microphone access denied. Please enable it in browser settings.'),
          'danger'
        );
      } else if (error.name === 'NotFoundError') {
        this.showToast(this.translate.instant('No microphone found. Please ensure a microphone is connected and enabled.'), 'danger');
      } else {
        this.showToast(this.translate.instant('Unable to access microphone. Check browser settings.'), 'danger');
      }
      console.error('Microphone Permission Error:', error);
      return null;
    }
  }

  private initSpeechRecognition() {
    const SpeechRecognition = (window as any).SpeechRecognition || 
                             (window as any).webkitSpeechRecognition || 
                             (window as any).mozSpeechRecognition || 
                             (window as any).msSpeechRecognition;
    
    if (!SpeechRecognition) {
      this.isSpeechSupported = false;
      return null;
    }

    const recognition = new SpeechRecognition();
    
    // Safari-specific configurations
    recognition.continuous = false; // Set to false for Safari compatibility
    recognition.interimResults = false; // Set to false for more reliable results in Safari
    recognition.lang = 'ta-MY';
    
    return recognition;
  }

  async startWebSpeechRecognition() {
    if (!this.isSpeechSupported) {
      this.showToast(this.translate.instant('Speech recognition is not supported in this browser.'), 'warning');
      return;
    }

    // Set listening state immediately
    this.isListening = true;
    this.isProcessing = false;
    this.cdr.detectChanges();

    try {
      const stream = await this.requestMicrophonePermission();
      if (!stream) {
        this.isListening = false;
        this.cdr.detectChanges();
        return;
      }
      
      this.recognition = this.initSpeechRecognition();
      if (!this.recognition) {
        this.showToast(this.translate.instant('Speech recognition initialization failed.'), 'warning');
        return;
      }

      // Update configuration for sentence mode
      if (this.mainSegment === 'sentence') {
        this.recognition.continuous = true; // Enable continuous mode for sentences
        this.recognition.interimResults = true; // Enable interim results
      } else {
        this.recognition.continuous = false;
        this.recognition.interimResults = false;
      }

      this.recognition.onstart = () => {
        this.isListening = true;
        this.isProcessing = false;
        this.cdr.detectChanges();
        this.showToast(this.translate.instant('Speak now!'), 'success');
      };
    
      this.recognition.onresult = (event: any) => {
        this.isListening = false;
        this.isProcessing = true;
        this.cdr.detectChanges();
        
        if (this.mainSegment === 'sentence') {
          this.handleRecognizedSentence(event);
        } else {
          const transcript = event.results[0][0].transcript.trim();
          this.handleRecognizedText(transcript);
        }
      };
    
      this.recognition.onerror = (event: any) => {
        console.error('Speech Recognition Error:', event.error);
        this.isListening = false;
        this.isProcessing = false;
        this.cdr.detectChanges();
        
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          this.showToast(this.translate.instant('Microphone access denied or speech recognition service not allowed. Please check browser permissions.'), 'danger');
        } else if (event.error === 'no-speech') {
          // Restart recognition on no-speech error
          if (this.isRecognizing) {
            this.debounceRestartRecognition();
          }
        } else {
          this.showToast(this.translate.instant('Recognition error: ') + event.error, 'danger');
        }
      };
    
      this.recognition.onend = () => {
        if (!this.isProcessing) {
          this.isListening = false;
          this.cdr.detectChanges();
        }
      };

      this.recognition.start();
      
    } catch (error) {
      console.error('Speech Recognition Error:', error);
      this.showToast(this.translate.instant('Failed to start speech recognition'), 'danger');
      this.isListening = false;
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  private stopWebSpeechRecognition() {
    if (this.recognition) {
      this.recognition.stop();
      this.recognition.abort();
      this.isListening = false;
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  private cleanAndFormatText(text: string): string {
    // Enhanced text cleaning
    return text
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[.,!?]/g, ''); // Remove punctuation
  }

  // Updated method to handle recognized text
  private async handleRecognizedText(text: string) {
    const cleanText = this.cleanAndFormatText(text);

    if (this.mainSegment === 'word' && this.currentLetter) {
      if (cleanText.toLowerCase().includes(this.currentLetter.word.toLowerCase())) {
        this.isCorrect = true;
        // Don't set isProcessing to false yet
        await this.playAudio('success');
        await this.showToast(this.translate.instant('Great! 👏'), 'success');
        
        // Keep isListening true during transition
        this.isListening = true;
        this.isProcessing = false;
        this.cdr.detectChanges();
        
        setTimeout(() => {
          this.selectNewLetter(false);
          // No need for nested setTimeout, just start recording right away
          if (this.useGoogleCloud) {
            this.startRecording();
          } else {
            this.startWebSpeechRecognition();
          }
        }, 1000);
      } else {
        this.isProcessing = false;
        this.cdr.detectChanges();
        await this.playAudio('wrong');
        await this.showToast(this.translate.instant('Please try again!'), 'warning');
        
        // Keep isListening true when restarting after wrong attempt
        this.isListening = true;
        this.cdr.detectChanges();
        
        setTimeout(() => {
          if (this.useGoogleCloud) {
            this.startRecording();
          } else {
            this.startWebSpeechRecognition();
          }
        }, 1500);
      }
    } else if (this.mainSegment === 'sentence' && 
               this.sentenceSubSegment === 'phrases' && 
               this.currentPhrase) {
      
      // Improved phrase matching logic
      const cleanPhrase = this.cleanAndFormatText(this.currentPhrase.tamil);
      const cleanRecognized = this.cleanAndFormatText(text);
      
      // Calculate similarity score
      const similarity = this.calculateSimilarity(cleanRecognized, cleanPhrase);
      const isMatch = similarity >= 0.8; // 80% similarity threshold

      if (this.DEBUG) {
        console.log('Phrase matching:', {
          recognized: cleanRecognized,
          expected: cleanPhrase,
          similarity: similarity,
          isMatch: isMatch
        });
      }

      try {
        if (isMatch) {
          this.isCorrect = true;
          await this.playAudio('success');
          await this.showToast(this.translate.instant('Excellent! 👏'), 'success', 1500);
          
          // Add to conversation history with mode
          this.conversationHistory.push({
            text: this.currentPhrase.tamil,
            isUser: true,
            timestamp: new Date(),
            transliteration: this.romanTransliterationPhrase,
            mode: 'phrases'
          });
          
          // Move to next phrase after a short delay
          setTimeout(async () => {
            await this.selectNewPhrase(false);
            // Start recording again automatically after a short delay
            setTimeout(() => {
              if (this.useGoogleCloud) {
                this.startRecording();
              }
            }, 500);
          }, 1000);
        } else {
          this.isCorrect = false;
          await this.playAudio('wrong');
          
          // Show helpful feedback based on similarity
          const feedback = this.generateFeedback(cleanRecognized, cleanPhrase);
          await this.showToast(feedback, 'warning', 2000);
          
          // Start recording again automatically after a short delay
          setTimeout(() => {
            if (this.useGoogleCloud) {
              this.startRecording();
            }
          }, 1500);
        }
      } catch (error) {
        console.error('Error handling phrase recognition:', error);
        await this.showToast(this.translate.instant('Error processing speech'), 'danger');
      }
    }
  }

  // Method to move to the next word
  private moveToNextWord() {
    setTimeout(() => {
      this.selectNewLetter(false); // Move to the next word without playing the click sound
    }, 1500); // Delay to allow the user to see the success feedback
  }

  // Updated method to select a new letter
  async selectNewLetter(playClickSound: boolean = true) {
    if (this.mainSegment !== 'word') return;

    // Select a random letter from the current letter set
    const randomIndex = Math.floor(Math.random() * this.letterSet.length);
    this.currentLetter = this.letterSet[randomIndex];
    this.isCorrect = false;
    this.buttonState = 'start';

    if (this.currentLetter) {
      this.romanTransliteration = this.transliterateToRoman(this.currentLetter.word);
    }
    
    if (playClickSound) {
      try {
        await this.playAudio('click');
      } catch (error) {
        console.warn('Could not play audio:', error);
      }
    }

    // Maintain isListening state during transitions
    if (this.isListening || this.isProcessing) {
      this.isListening = true;
      this.isProcessing = false;
    }
    
    this.cdr.detectChanges();
  }

  private async handleRecognizedSentence(event: any) {
    const results = event.results;
    const lastResult = results[results.length - 1];
    
    if (lastResult.isFinal) {
      const text = lastResult[0].transcript.trim();
      
      if (!text) return;

      const lastMessage = this.getFilteredConversationHistory().slice(-1)[0];
      if (!lastMessage || lastMessage.text !== text) {
        // Add the recognized speech to conversation history with current mode
        this.conversationHistory.push({ 
          text, 
          isUser: true, 
          timestamp: new Date(),
          transliteration: this.transliterateToRoman(text),
          mode: this.sentenceSubSegment // Set the current mode
        });

        await this.playAudio('success');
        
        // Keep only last 10 messages of the current mode
        const filteredHistory = this.getFilteredConversationHistory();
        if (filteredHistory.length > 10) {
          this.conversationHistory = this.conversationHistory.filter(msg => 
            msg.mode !== this.sentenceSubSegment || 
            filteredHistory.slice(-10).includes(msg)
          );
        }

        this.cdr.detectChanges();
      }
    }

    if (!this.recognition.continuous) {
      this.debounceRestartRecognition();
    }
  }

  private debounceRestartRecognition() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.debounceTimeout = setTimeout(() => {
      if (this.isRecognizing && this.mainSegment === 'sentence') {
        this.recognition.start();
      }
    }, 100); // Adjust the delay as needed
  }

 

  async speakResponse(text: string | undefined) {
    if (!text) return;

    try {
      if ('speechSynthesis' in window) {
        // Cancel any ongoing speech
        window.speechSynthesis.cancel();
        
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'ta-MY';
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        
        window.speechSynthesis.speak(utterance);
      } else {
        this.showToast(this.translate.instant('Text-to-speech not supported in this browser'), 'warning');
      }
    } catch (error) {
      console.error('Speech synthesis error:', error);
      this.showToast(this.translate.instant('Failed to play audio'), 'warning');
    }
  }

  async startSpeechRecognition() {
    if (this.isRecognizing) {
      this.stopWebSpeechRecognition();
      this.isRecognizing = false;
      this.buttonState = 'next';
      return;
    }

    try {
      await this.startWebSpeechRecognition();
      this.isRecognizing = true;
      this.buttonState = 'next';
    } catch (error) {
      console.error('Speech Recognition Error:', error);
      this.showToast(this.translate.instant('Failed to start speech recognition'), 'danger');
      this.isRecognizing = false;
      this.buttonState = 'start';
    }
  }

  async handleMainButton() {
    await this.handleUserInteraction();
    
    // If already recording/listening, stop everything
    if (this.isListening || this.isProcessing) {
      // Reset states first
      this.isListening = false;
      this.isProcessing = false;
      this.buttonState = 'start';
      this.isRecognizing = false;
      
      // Then stop the recognition/recording
      if (this.useGoogleCloud) {
        this.stopRecording();
      } else {
        this.stopWebSpeechRecognition();
      }
      
      this.cdr.detectChanges();
      return;
    }
    
    // If not recording/listening, start new recording
    if (this.useGoogleCloud) {
      if (!this.isRecording) {
        await this.startRecording();
      }
    } else {
      if (this.buttonState === 'start') {
        await this.startSpeechRecognition();
      }
    }
  }

  onMainSegmentChange(event: any) {
    // Stop any ongoing listening/recording first
    if (this.useGoogleCloud) {
      this.stopRecording();
    } else {
      this.stopWebSpeechRecognition();
    }
    
    // Reset states
    this.isListening = false;
    this.isProcessing = false;
    this.buttonState = 'start';

    // Continue with existing segment change logic
    this.mainSegment = event.detail.value;
    if (this.mainSegment === 'sentence') {
      this.currentLetter = null;
      if (this.sentenceSubSegment === 'phrases') {
        this.selectNewPhrase(true);
      }
    } else {
      this.currentPhrase = null;
      this.letterSet = this.getCurrentLetterSet(this.wordSubSegment);
      this.selectNewLetter(true);
    }

    this.cdr.detectChanges();
  }

  onWordSubSegmentChange(event: any) {
    // Stop any ongoing listening/recording first
    if (this.useGoogleCloud) {
      this.stopRecording();
    } else {
      this.stopWebSpeechRecognition();
    }
    
    // Reset states
    this.isListening = false;
    this.isProcessing = false;
    this.buttonState = 'start';

    // Continue with existing sub-segment change logic
    this.wordSubSegment = event.detail.value;
    this.letterSet = this.getCurrentLetterSet(this.wordSubSegment);
    this.selectNewLetter(true);

    this.cdr.detectChanges();
  }

  private getCurrentLetterSet(type: 'uyir' | 'mei'): Letter[] {
    return type === 'uyir' ? uyirLetters : meiLetters;
  }

  async startRecording() {
    try {
      // Set listening state immediately
      this.isListening = true;
      this.isProcessing = false;
      this.cdr.detectChanges();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioChunks = [];
      
      const mimeType = this.getSupportedMimeType();
      
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000
      });

      // Set up audio analysis for speech detection
      if (this.audioContext) {
        const source = this.audioContext.createMediaStreamSource(stream);
        this.audioAnalyser = this.audioContext.createAnalyser();
        this.audioAnalyser.fftSize = 256;
        source.connect(this.audioAnalyser);
        
        this.analyserData = new Uint8Array(this.audioAnalyser.frequencyBinCount);
        this.startSilenceDetection();
      }
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        this.isListening = false;
        this.isProcessing = true;
        this.cdr.detectChanges();
        
        this.stopSpeechDetection();
        stream.getTracks().forEach(track => track.stop());
        
        if (this.audioChunks.length > 0) {
          await this.sendToGoogleSpeechAPI();
        }
      };

      // Start recording
      this.mediaRecorder.start(100);
      await this.playAudio('click');
      
      // Auto-stop after silence
      this.startSilenceDetection();

    } catch (error) {
      console.error('Error starting recording:', error);
      this.showToast('Error starting recording. Please try again.', 'danger');
      this.isListening = false;
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  private startSilenceDetection() {
    if (!this.audioAnalyser || !this.analyserData) return;

    let silenceStart: number | null = null;
    const SILENCE_THRESHOLD = 20; // Adjust as needed
    const SILENCE_DURATION = 1500; // 1.5 seconds of silence before stopping

    const checkAudioLevel = () => {
      if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') return;

      this.audioAnalyser!.getByteFrequencyData(this.analyserData!);
      const average = this.analyserData!.reduce((a, b) => a + b) / this.analyserData!.length;

      if (average < SILENCE_THRESHOLD) {
        if (!silenceStart) {
          silenceStart = Date.now();
        } else if (Date.now() - silenceStart > SILENCE_DURATION) {
          this.stopRecording();
          return;
        }
      } else {
        silenceStart = null;
      }

      requestAnimationFrame(checkAudioLevel);
    };

    // Start checking audio levels
    requestAnimationFrame(checkAudioLevel);

    // Set maximum recording duration (15 seconds)
    this.recordingTimeout = setTimeout(() => {
      if (this.mediaRecorder?.state === 'recording') {
        this.stopRecording();
      }
    }, 15000);
  }

  async stopRecording() {
    if (this.recordingTimeout) {
      clearTimeout(this.recordingTimeout);
      this.recordingTimeout = null;
    }

    if (this.mediaRecorder?.state === 'recording') {
      this.isProcessing = true;
      this.mediaRecorder.stop();
      this.stopSpeechDetection();
    }
  }

  // Add this method to properly clean up resources
  private cleanupRecording() {
    if (this.mediaRecorder) {
      if (this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.stop();
      }
      this.stopSpeechDetection();
      this.audioChunks = [];
      this.mediaRecorder = undefined;
    }
    this.isProcessing = false;
  }

  private async sendToGoogleSpeechAPI() {
    if (this.audioChunks.length === 0) {
      this.showToast('No audio recorded', 'warning');
      return;
    }

    this.isProcessing = true;
    const loading = await this.loadingController.create({
      message: this.translate.instant('Processing speech...'),
      duration: 10000
    });
    await loading.present();

    try {
      const audioBlob = new Blob(this.audioChunks, { 
        type: this.getSupportedMimeType() || 'audio/webm;codecs=opus' 
      });
      
      if (audioBlob.size < 1000) {
        await loading.dismiss();
        this.showToast(this.translate.instant('Speech too short, please try again'), 'warning');
        // Reset states here too
        this.isProcessing = false;
        this.isListening = false;
        this.buttonState = 'start';
        this.cdr.detectChanges();
        return;
      }

      const base64Audio = await this.blobToBase64(audioBlob);
      const requestBody = {
        config: {
          encoding: 'WEBM_OPUS',
          sampleRateHertz: 48000,
          languageCode: 'ta-IN',
          model: 'default',
          enableAutomaticPunctuation: true,
          useEnhanced: true
        },
        audio: {
          content: base64Audio.split(',')[1]
        }
      };

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': environment.googleCloud.apiKey
      });

      const response = await this.http.post(
        environment.googleCloud.speechToTextEndpoint,
        requestBody,
        { headers }
      ).toPromise();

      if (response && (response as any).results?.[0]) {
        const transcription = (response as any).results[0].alternatives[0].transcript;
        
        if (this.mainSegment === 'word' || 
            (this.mainSegment === 'sentence' && this.sentenceSubSegment === 'phrases')) {
          // Handle both word and phrase recognition
          await this.handleRecognizedText(transcription);
        } else {
          // Handle freeflow conversation
          this.conversationHistory.push({
            text: transcription,
            isUser: true,
            timestamp: new Date(),
            transliteration: this.transliterateToRoman(transcription),
            mode: 'freeflow'
          });
          await this.playAudio('success');
          
          // Reset states for freeflow mode
          this.isProcessing = false;
          this.isListening = false;
          this.buttonState = 'start';
        }
        
        // Force view update
        this.cdr.detectChanges();
      } else {
        this.showToast(this.translate.instant('No speech detected. Please try again.'), 'warning');
        // Reset states here too
        this.isProcessing = false;
        this.isListening = false;
        this.buttonState = 'start';
        this.cdr.detectChanges();
      }
    } catch (error: any) {
      console.error('Speech API error:', error);
      
      if (error.status === 400) {
        this.showToast(this.translate.instant('Invalid audio format. Please try again.'), 'danger');
      } else if (error.status === 403) {
        this.showToast(this.translate.instant('API key error. Please check configuration.'), 'danger');
      } else {
        this.showToast(this.translate.instant('Failed to process speech. Please try again.'), 'danger');
      }
      // Reset states here too
      this.isProcessing = false;
      this.isListening = false;
      this.buttonState = 'start';
      this.cdr.detectChanges();
    } finally {
      this.isProcessing = false;
      await loading.dismiss();
      this.cleanupRecording();
      // Make sure states are reset
      this.isListening = false;
      this.buttonState = 'start';
      this.cdr.detectChanges();
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private handleRecognizedSpeech(transcription: string) {
    // Your existing logic to handle the recognized speech
    // Compare with word list, update score, etc.
  }

  ngOnInit() {
    this.letterSet = this.getCurrentLetterSet('uyir');
    this.selectNewLetter();
    this.sentenceSubSegment = 'phrases'; // Set default sub-segment
  }
  
  ngOnDestroy() {
    this.stopWebSpeechRecognition();
    this.cleanupRecording();
    this.stopSpeechDetection();
    if (this.audioContext) {
      this.audioContext.close();
      this._audioInitialized = false;
    }
  }

  // Add this method to your SpeakPage class
  clearConversation() {
    const currentMode = this.sentenceSubSegment;
    this.conversationHistory = this.conversationHistory.filter(msg => msg.mode !== currentMode);
    this.showToast(this.translate.instant('Conversation cleared.'), 'success');
  }

  async toggleSpeechAPI() {
    // Prevent switching to Web Speech API on mobile devices
    if (this.isMobileDevice && !this.useGoogleCloud) {
      const alert = await this.alertController.create({
        header: this.translate.instant('Not Recommended'),
        message: this.translate.instant('Web Speech API may not work reliably on mobile devices. We recommend using Google Cloud for better accuracy.'),
        buttons: [
          {
            text: this.translate.instant('Continue with Google Cloud'),
            role: 'cancel',
            handler: () => {
              this.useGoogleCloud = true;
            }
          },
          {
            text: this.translate.instant('Switch Anyway'),
            handler: () => {
              this.useGoogleCloud = false;
            }
          }
        ]
      });
      await alert.present();
      return;
    }

    // For desktop or switching back to Google Cloud
    if (!this.useGoogleCloud) {
      // Verify setup before switching to Google Cloud
      const isValid = await this.verifyGoogleCloudSetup();
      if (!isValid) {
        return;
      }
    }
    
    this.useGoogleCloud = !this.useGoogleCloud;
    if (this.isRecording) {
      this.useGoogleCloud ? this.stopRecording() : this.stopWebSpeechRecognition();
    }
  }

  async testMicrophoneAccess() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.showToast(this.translate.instant('Microphone access successful!'), 'success');
      // Remember to stop the stream
      stream.getTracks().forEach(track => track.stop());
    } catch (error: any) {
      console.error('Microphone test failed:', error);
      this.showToast(this.translate.instant('Microphone test failed: ') + error.message, 'danger');
    }
  }

  // Add method to check supported MIME types
  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return '';
  }

  async testGoogleCloudAPI() {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('X-Goog-Api-Key', environment.googleCloud.apiKey);

    try {
      // Test with a simple request
      const response = await this.http.post(
        'https://speech.googleapis.com/v1/speech:recognize',
        {
          config: {
            encoding: 'LINEAR16',
            sampleRateHertz: 16000,
            languageCode: 'ta-IN'
          },
          audio: {
            content: '' // Empty content for testing
          }
        },
        { headers }
      ).toPromise();
      
      this.showToast(this.translate.instant('API connection successful'), 'success');
    } catch (error: any) {
      console.error('API test error:', error);
      const errorMessage = error?.error?.error?.message || error?.message || 'Unknown error';
      this.showToast(this.translate.instant('API test failed: ') + errorMessage, 'danger');
    }
  }

  async verifyGoogleCloudSetup() {
    try {
      const headers = new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('X-Goog-Api-Key', environment.googleCloud.apiKey);

      const response = await this.http.post(
        environment.googleCloud.speechToTextEndpoint,
        {
          config: {
            encoding: 'LINEAR16',
            sampleRateHertz: 16000,
            languageCode: 'ta-IN'
          },
          audio: {
            content: 'test'
          }
        },
        { 
          headers,
          observe: 'response'
        }
      ).toPromise();

      return true;
    } catch (error: any) {
      console.error('API verification error:', error);
      if (error.status === 403) {
        this.showToast(this.translate.instant('API key is invalid or does not have proper permissions'), 'danger');
      } else if (error.status === 0) {
        this.showToast(this.translate.instant('Network error. Please check CORS settings'), 'danger');
      }
      return false;
    }
  }

  // Add method to handle user interaction
  async handleUserInteraction() {
    if (!this._audioInitialized) {
      await this.initAudio();
    }
  }

  // Add this method to the class
  private stopSpeechDetection() {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    this.isUserSpeaking = false;
    this.audioAnalyser?.disconnect();
    this.audioAnalyser = null;
    this.analyserData = null;
  }

  // Add the transliteration method (copy from type.page.ts)
  private transliterateToRoman(tamilText: string): string {
    // Mapping for individual characters
    const tamilToRomanMap: { [key: string]: string } = {
      'அ': 'a', 'ஆ': 'ā', 'இ': 'i', 'ஈ': 'ī', 
      'உ': 'u', 'ஊ': 'ū', 'எ': 'e', 'ஏ': 'ē', 
      'ஐ': 'ai', 'ஒ': 'o', 'ஓ': 'ō', 'ஔ': 'au', 
      // Mei without inherent 'a' sound
      'க்': 'k', 'ங்': 'ṅg', 'ச்': 'c', 'ஞ்': 'ñj', 
      'ட்': 'd', 'ண்': 'ṇ', 'த்': 't', 'ந்': 'n', 
      'ப்': 'p', 'ம்': 'm', 'ய்': 'y', 'ர்': 'r', 
      'ல்': 'l', 'வ்': 'v', 'ழ்': 'ḻh', 'ள்': 'ḷ', 
      'ற்': 'ṟ', 'ன்': 'ṉ',
      // Mei with inherent 'a' sound
      'க': 'ka', 'ங': 'ṅga', 'ச': 'ca', 'ஞ': 'ñja', 
      'ட': 'da', 'ண': 'ṇa', 'த': 'ta', 'ந': 'na', 
      'ப': 'pa', 'ம': 'ma', 'ய': 'ya', 'ர': 'ra', 
      'ல': 'la', 'வ': 'va', 'ழ': 'ḻha', 'ள': 'ḷa', 
      'ற': 'ṟa', 'ன': 'ṉa',
    };

    // Mapping for uyir markers
    const uyirMarkerMap: { [key: string]: string } = {
      'ா': 'ā', 'ி': 'i', 'ீ': 'ī', 'ு': 'u', 'ூ': 'ū',
      'ெ': 'e', 'ே': 'ē', 'ை': 'ai', 'ொ': 'o', 'ோ': 'ō', 'ௌ': 'au',
      '்': '', // Pulli, used to suppress the inherent uyir
    };

    let transliteratedText = '';
    let i = 0;

    while (i < tamilText.length) {
      const currentChar = tamilText[i];
      const nextChar = tamilText[i + 1];

      if (uyirMarkerMap[nextChar] !== undefined) {
        // Combine consonant with uyir marker
        const combined = tamilToRomanMap[currentChar + '்'] + uyirMarkerMap[nextChar];
        transliteratedText += combined || currentChar + nextChar;
        i += 2; // Skip the next character as it's a uyir marker
      } else {
        // Single character, just transliterate
        transliteratedText += tamilToRomanMap[currentChar] || currentChar;
        i++;
      }
    }

    return transliteratedText;
  }

  // Add new method for sentence sub-segment changes
  onSentenceSubSegmentChange(event: any) {
    // Stop any ongoing listening/recording first
    if (this.useGoogleCloud) {
      this.stopRecording();
    } else {
      this.stopWebSpeechRecognition();
    }
    
    // Reset states
    this.isListening = false;
    this.isProcessing = false;
    this.buttonState = 'start';

    // Continue with existing sub-segment change logic
    this.sentenceSubSegment = event.detail.value;
    
    if (this.sentenceSubSegment === 'phrases') {
      this.selectNewPhrase(true);
    } else {
      this.currentPhrase = null;
    }
    
    this.cdr.detectChanges();
  }

  // Add method to select new phrase
  async selectNewPhrase(playClickSound: boolean = true) {
    if (this.mainSegment !== 'sentence' || this.sentenceSubSegment !== 'phrases') return;

    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * commonPhrases.length);
    } while (
      this.currentPhrase && 
      this.currentPhrase.tamil === commonPhrases[randomIndex].tamil
    );

    this.currentPhrase = commonPhrases[randomIndex];
    this.isCorrect = false;
    this.buttonState = 'start';

    if (this.currentPhrase) {
      this.romanTransliterationPhrase = this.transliterateToRoman(this.currentPhrase.tamil);
    }

    if (playClickSound) {
      try {
        await this.playAudio('click');
      } catch (error) {
        console.warn('Could not play audio:', error);
      }
    }

    this.cdr.detectChanges();

    // Update recording state for automatic transitions
    if (this.useGoogleCloud ? 
        (this.mediaRecorder?.state === 'recording' || this.isProcessing) : 
        this.isRecognizing) {
      if (this.useGoogleCloud) {
        await this.startRecording();
      } else {
        this.stopWebSpeechRecognition();
        this.startWebSpeechRecognition();
      }
    }
  }

  // Add these new helper methods

  private calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) {
      return 1.0;
    }
    
    // Calculate Levenshtein distance
    const costs: number[] = [];
    for (let i = 0; i <= shorter.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= longer.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (shorter[i - 1] !== longer[j - 1]) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) {
        costs[longer.length] = lastValue;
      }
    }
    
    const distance = costs[longer.length];
    return (longer.length - distance) / longer.length;
  }

  private generateFeedback(recognized: string, expected: string): string {
    const similarity = this.calculateSimilarity(recognized, expected);
    
    if (similarity > 0.6) {
      return this.translate.instant('Almost there! Try again.');
    } else if (similarity > 0.4) {
      return this.translate.instant('Getting closer! Keep trying.');
    } else {
      return this.translate.instant('Listen carefully and try again.');
    }
  }

  // Add this method to the SpeakPage class
  getFilteredConversationHistory(): Array<{ 
    text: string, 
    isUser: boolean, 
    timestamp: Date,
    transliteration?: string,
    mode?: 'freeflow' | 'phrases'
  }> {
    return this.conversationHistory.filter(msg => 
      msg.mode === this.sentenceSubSegment && 
      msg.mode !== undefined
    );
  }

  // Add this method to check if filtered history is empty
  hasFilteredMessages(): boolean {
    return this.getFilteredConversationHistory().length > 0;
  }

  // Add new method to play current audio
  async playCurrentAudio() {
    if (this.mainSegment === 'word' && this.currentLetter) {
      await this.speakResponse(this.currentLetter.word);
    } else if (this.mainSegment === 'sentence' && 
               this.sentenceSubSegment === 'phrases' && 
               this.currentPhrase) {
      await this.speakResponse(this.currentPhrase.tamil);
    }
  }
}